import { useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router";

import { useNotification } from "@refinedev/core";
import { Loader2 } from "lucide-react";

import { useFestivalRepository } from "@/pages/tina/festivals/hooks/use-festival-repository";
import { LIST_PATH, pagePath } from "@/pages/tina/festivals/lib/paths";

/** An old record url (records, then the id) opens that festival's page: the record id is the page's id. */
export const FestivalRecordRedirect = () => {
  const { id = "" } = useParams<{ id: string }>();
  return <Navigate to={pagePath(id)} replace />;
};

/**
 * An old card url (cards, then the id), from a Discord message or a bookmark: the card knows its festival, so
 * the page opens on it. A card that is gone lands on the list, with a sentence that says why.
 */
export const FestivalCardRedirect = () => {
  const { id = "" } = useParams<{ id: string }>();
  const repo = useFestivalRepository();
  const navigate = useNavigate();
  const { open } = useNotification();

  useEffect(() => {
    let cancelled = false;
    repo.fetchCardRecordId(id).then(
      (recordId) => {
        if (!cancelled) void navigate(pagePath(recordId), { replace: true });
      },
      () => {
        if (cancelled) return;
        open?.({
          type: "error",
          message: "That card is gone. Its festival is in this list.",
        });
        void navigate(LIST_PATH, { replace: true });
      },
    );
    return () => {
      cancelled = true;
    };
    // The redirect is decided once per card id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <p className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Opening the festival…
    </p>
  );
};
